import type React from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import Typography from 'app/ui/components/atoms/typography';
import type { GroupContentItemData } from 'app/ui/components/molecules/content-item-areas-wrapper';
import { LessonSectionFormat } from 'src/app/types/graphql';

type GroupBreakdownProps = {
  group: GroupContentItemData;
  index: number;
  isSingleGroup?: boolean;
  isSmallVariant?: boolean;
  isOverviewMode?: boolean;
  className?: string;
};

const GroupWithHeader = styled.div<{ $isSmallVariant?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing }, $isSmallVariant }) => ($isSmallVariant ? spacing(4) : '0')};
`;

const GroupWrapper = styled.div<{ $isSmallVariant?: boolean }>`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing(4.5)};
  margin: ${({ theme: { spacing }, $isSmallVariant }) => ($isSmallVariant ? '0' : `${spacing(2)} 0`)};
`;

const PartnerWorkoutGroupWrapper = styled(GroupWrapper)`
  margin-bottom: -${({ theme: { spacing } }) => spacing(1)};
`;

const ZoneColumn = styled.div<{ $isSmallVariant?: boolean }>`
  align-items: flex-start;
  margin-right: ${({ theme, $isSmallVariant }) => ($isSmallVariant ? 0 : theme.spacing(1))};
  min-width: 5rem;

  ${({ theme: { media }, $isSmallVariant }) => `${media.medium(
    () => `
      min-width: ${$isSmallVariant ? '5rem' : '7.5rem'};
    `,
  )}
  `};
`;

const ZoneTitle = styled(Typography)`
  font-weight: ${({ theme }) => theme.typography.bold};
`;

const ExercisesColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(1.5)};
`;

const ExerciseWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ExerciseDetailsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
`;

const GroupNote = styled(Typography)`
  margin: 0;
`;

const Note = styled(Typography)<{ $isSingleGroup?: boolean; $isBottomNote?: boolean }>`
  margin-top: ${({ theme, $isBottomNote }) => ($isBottomNote ? 0 : theme.spacing(1.75))};
  margin-bottom: ${({ theme, $isBottomNote }) => ($isBottomNote ? theme.spacing(1.75) : 0)};
  margin-left: ${({ theme: { spacing }, $isSingleGroup }) =>
    $isSingleGroup ? `calc(${spacing(10)} + ${spacing(4)});` : 0};
`;

const IndexNumber = styled(Typography)`
  white-space: nowrap;
  flex-shrink: 0;
  min-width: ${({ theme }) => theme.spacing(10)};
  margin-right: ${({ theme }) => theme.spacing(4)};
`;

const ExerciseName = styled(Typography)`
  flex: 1;
`;

const GroupBreakdown: React.FC<GroupBreakdownProps> = ({
  group,
  isSingleGroup,
  isSmallVariant,
  isOverviewMode,
  className,
}) => {
  const theme = useTheme();
  const intl = useIntl();

  const applySmallVariant = isSmallVariant || isOverviewMode;

  const textVariant = applySmallVariant ? 'whiteboard2' : 'whiteboard1';

  const frtText = intl.formatMessage({ id: 'whiteboard.frt' });

  const directExercises = group.nestedGroups?.length
    ? group.sectionExercises.filter(
        (exercise) =>
          !group.nestedGroups?.some((nestedGroup) =>
            nestedGroup.sectionExercises.some((nestedEx) => nestedEx.id === exercise.id),
          ),
      )
    : group.sectionExercises;

  const hasNestedGroups = group.nestedGroups && group.nestedGroups.length > 0;
  const shouldShowGroupTitle = !isSingleGroup && !hasNestedGroups;

  return (
    <GroupWithHeader $isSmallVariant={applySmallVariant}>
      {group.template?.format === LessonSectionFormat.PARTNER_WORKOUT && (
        <PartnerWorkoutGroupWrapper>
          <ZoneColumn $isSmallVariant={applySmallVariant}>
            <ZoneTitle variant={textVariant} color={theme.colors.focoltone}>
              {group.title}
            </ZoneTitle>
          </ZoneColumn>
        </PartnerWorkoutGroupWrapper>
      )}
      <GroupWrapper $isSmallVariant={applySmallVariant} className={className}>
        {shouldShowGroupTitle && (
          <ZoneColumn $isSmallVariant={applySmallVariant}>
            <ZoneTitle variant={textVariant} color={theme.colors.focoltone}>
              {group.title}
            </ZoneTitle>
          </ZoneColumn>
        )}
        <ExercisesColumn>
          {!isOverviewMode && group.notes && group.notes.length > 0 && (
            <GroupNote variant={textVariant} color={theme.colors.focoltone}>
              {group.notes}
            </GroupNote>
          )}
          {directExercises.map((exercise, i) => {
            const isForRemainingTime = exercise.additionalInfo === 'FRT';
            const shouldShowAdditionalInfo = !isForRemainingTime || isOverviewMode;

            return (
              <ExerciseWrapper key={`exercise-${exercise.id}`}>
                <ExerciseDetailsWrapper data-testid={`exercise-details-wrapper-${i}`}>
                  {isSingleGroup && !hasNestedGroups && (
                    <IndexNumber variant={textVariant} color={theme.colors.focoltone}>
                      #{i + 1}
                    </IndexNumber>
                  )}
                  <ExerciseName
                    variant={textVariant}
                    color={theme.colors.white}
                  >{`${shouldShowAdditionalInfo ? exercise.additionalInfo : ''} ${exercise.title} ${exercise.rpe ? `@RPE ${exercise.rpe}` : ''}`}</ExerciseName>
                </ExerciseDetailsWrapper>
                {!isOverviewMode && exercise?.notes && exercise.notes.length > 0 && (
                  <Note
                    $isSingleGroup={isSingleGroup || false}
                    variant={textVariant}
                    color={theme.colors.lightGray}
                    $isBottomNote={true}
                  >
                    {exercise.notes}
                  </Note>
                )}
                {!isOverviewMode && isForRemainingTime && (
                  <Note variant={textVariant} color={theme.colors.lightGray} $isBottomNote={true}>
                    {frtText}
                  </Note>
                )}
              </ExerciseWrapper>
            );
          })}
          {group.nestedGroups?.map((nestedGroup, nestedIndex) => (
            <NestedGroupBreakdown
              key={`nested-${nestedGroup.id}`}
              group={nestedGroup}
              index={nestedIndex + 1}
              isSingleGroup={false}
              isSmallVariant={isSmallVariant}
              isOverviewMode={isOverviewMode}
            />
          ))}
        </ExercisesColumn>
      </GroupWrapper>
    </GroupWithHeader>
  );
};

const NestedGroupBreakdown = styled(GroupBreakdown)`
  margin-bottom: ${({ theme: { spacing } }) => spacing(2)};
`;

export default GroupBreakdown;
